/**
 * TV Navigation Manager
 * Implements TV remote and keyboard navigation best practices:
 * 1. "Atrás" (Escape / Back) key priority: always closes open dialogs/panels first before route navigation.
 * 2. Visible focus for all actions on TV / remote controller.
 * 3. Predictable spatial navigation order for D-pad arrows.
 */

export class TvNavigationManager {
  private static instance: TvNavigationManager;
  private listening = false;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  public static getInstance(): TvNavigationManager {
    if (!TvNavigationManager.instance) {
      TvNavigationManager.instance = new TvNavigationManager();
    }
    return TvNavigationManager.instance;
  }

  private constructor() {
    this.init();
  }

  public init() {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;

    this.keydownHandler = (event: KeyboardEvent) => {
      // 1. Detect if Back / Escape pressed (support Escape, Back, Android keyCode 4, Tizen 10009, WebOS 461)
      const isBackKey =
        event.key === 'Escape' ||
        event.key === 'Back' ||
        event.key === 'GoBack' ||
        event.key === 'BrowserBack' ||
        event.keyCode === 27 ||
        event.keyCode === 4 ||
        event.keyCode === 10009 ||
        event.keyCode === 461;

      if (isBackKey) {
        const handled = this.handleBackKey(event);
        if (handled) return;
      }

      // 2. Mark TV navigation mode when arrow keys or D-pad are used
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        document.documentElement.dataset.yomiriTv = 'true';
      }
    };

    window.addEventListener('keydown', this.keydownHandler, true);
  }

  /**
   * Handles "Atrás" (Escape/Back) key.
   * Priority: closes open dialog, drawer, menu, or modal FIRST without requiring a mouse.
   * Returns true if a modal was closed (preventing route change).
   */
  public handleBackKey(event?: KeyboardEvent): boolean {
    if (typeof document === 'undefined') return false;

    // Selector for open dialogs, drawers, popovers, menus or modals
    const dialogSelectors = [
      '.MuiDialog-root:not([aria-hidden="true"])',
      '.MuiDrawer-root:not([aria-hidden="true"])',
      '.MuiPopover-root:not([aria-hidden="true"])',
      '.MuiMenu-root:not([aria-hidden="true"])',
      'dialog[open]',
      '[role="dialog"]:not([aria-hidden="true"])',
      '.y-modal',
      '.y-repositories-dialog',
    ];

    const openDialogs = Array.from(document.querySelectorAll<HTMLElement>(dialogSelectors.join(',')))
      .filter((el) => {
        try {
          const style = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(el) : null;
          if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
          if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
          return true;
        } catch {
          return true;
        }
      });

    if (openDialogs.length > 0) {
      // Top-most dialog is the last one in DOM
      const topDialog = openDialogs[openDialogs.length - 1];

      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const buttons = Array.from(topDialog.querySelectorAll<HTMLElement>('button:not(:disabled)'));

      // 1. Explicit Cancel or Close button by text, title or aria-label
      const cancelOrCloseBtn = buttons.find((b) => {
        const text = (b.textContent || '').trim().toLowerCase();
        const label = (b.getAttribute('aria-label') || '').toLowerCase();
        const title = (b.getAttribute('title') || '').toLowerCase();
        const matchesKeyword =
          /^(cancelar|cancel|cerrar|close|volver|descartar|salir)$/i.test(text) ||
          /cancelar|cancel|cerrar|close|volver|descartar|dismiss/i.test(label) ||
          /cancelar|cancel|cerrar|close|volver|descartar|dismiss/i.test(title);
        const hasCloseIcon =
          (typeof b.classList?.contains === 'function' && b.classList.contains('MuiDialog-close')) ||
          (typeof b.querySelector === 'function' &&
            b.querySelector('svg[data-testid="CloseIcon"], svg[data-testid="CloseOutlinedIcon"], svg.lucide-x') !== null);
        return matchesKeyword || hasCloseIcon;
      });

      if (cancelOrCloseBtn && typeof cancelOrCloseBtn.click === 'function') {
        cancelOrCloseBtn.click();
        return true;
      }

      // 2. If single button present (e.g. alert modal "OK" or "Cerrar"), click it
      if (buttons.length === 1 && typeof buttons[0].click === 'function') {
        buttons[0].click();
        return true;
      }

      // 3. Native dialog close method
      if (topDialog instanceof HTMLDialogElement && typeof topDialog.close === 'function') {
        topDialog.close();
        return true;
      }

      // 4. Trigger synthetic click on backdrop if present
      const backdrop = topDialog.querySelector<HTMLElement>('.MuiBackdrop-root, .MuiDialog-container');
      if (backdrop && typeof backdrop.click === 'function') {
        backdrop.click();
        return true;
      }

      // 5. Dispatch Escape key directly on dialog to let standard MUI/native listeners dismiss it
      topDialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true }));
      return true;
    }

    return false;
  }

  /**
   * Spatial D-pad navigation helper to find the nearest element in direction.
   */
  public handleDpadNavigation(direction: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'): boolean {
    if (typeof document === 'undefined') return false;

    const active = document.activeElement as HTMLElement | null;
    const focusables = Array.from(
      document.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]:not([inert])'
      )
    ).filter((el) => el.getClientRects().length > 0 && !el.closest('[aria-hidden="true"], [inert]'));

    if (focusables.length === 0) return false;

    if (!active || !focusables.includes(active)) {
      focusables[0].focus();
      return true;
    }

    const currentRect = active.getBoundingClientRect();
    const currentCenter = {
      x: currentRect.left + currentRect.width / 2,
      y: currentRect.top + currentRect.height / 2,
    };

    let bestCandidate: HTMLElement | null = null;
    let minDistance = Infinity;

    for (const el of focusables) {
      if (el === active) continue;
      const rect = el.getBoundingClientRect();
      const center = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };

      const dx = center.x - currentCenter.x;
      const dy = center.y - currentCenter.y;

      let isValidDirection = false;
      if (direction === 'ArrowUp' && dy < -5) isValidDirection = true;
      if (direction === 'ArrowDown' && dy > 5) isValidDirection = true;
      if (direction === 'ArrowLeft' && dx < -5) isValidDirection = true;
      if (direction === 'ArrowRight' && dx > 5) isValidDirection = true;

      if (!isValidDirection) continue;

      // Weight primary direction over orthogonal axis for predictable linear movement
      const weightX = direction === 'ArrowUp' || direction === 'ArrowDown' ? 2.5 : 1;
      const weightY = direction === 'ArrowLeft' || direction === 'ArrowRight' ? 2.5 : 1;
      const distance = Math.hypot(dx * weightX, dy * weightY);

      if (distance < minDistance) {
        minDistance = distance;
        bestCandidate = el;
      }
    }

    if (bestCandidate) {
      bestCandidate.focus();
      bestCandidate.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      return true;
    }

    return false;
  }

  public destroy() {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }
    this.listening = false;
  }
}

export const tvNavigationManager = TvNavigationManager.getInstance();
